import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { adminAuth } from "@/lib/adminAuth";

export default function ChangePassword() {
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => adminAuth.changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setError(null);
      setCurrent(""); setNext(""); setConfirm("");
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Change failed."),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("New password and confirmation do not match."); return; }
    if (next === current) { setError("New password must be different from the current one."); return; }
    save.mutate();
  };

  return (
    <main className="mx-auto max-w-md p-6">
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-brand-ink">Change password</h1>
        <p className="mt-1 text-sm text-brand-ink/60">
          Signed in as <b>{me.data?.email ?? "…"}</b>
        </p>

        {done ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800">
              ✓ Your password has been updated.
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setDone(false)}>Change again</button>
              <button className="btn-primary" onClick={() => navigate("/admin")}>Back to dashboard</button>
            </div>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <label className="field-label" htmlFor="current">Current password</label>
              <input id="current" className="field-input" type="password"
                autoComplete="current-password" required
                value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="next">New password</label>
              <input id="next" className="field-input" type="password"
                autoComplete="new-password" minLength={8} required
                value={next} onChange={(e) => setNext(e.target.value)} />
              <p className="mt-1 text-xs text-brand-ink/60">At least 8 characters.</p>
            </div>
            <div>
              <label className="field-label" htmlFor="confirm">Confirm new password</label>
              <input id="confirm" className="field-input" type="password"
                autoComplete="new-password" minLength={8} required
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>

            {error && <div className="text-sm text-brand" role="alert">{error}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={save.isPending}>
                {save.isPending ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
